// CardMarket and eBay API connectors
// Connects to Firebase proxy for CardMarket and provides market data

import { cardMarketClient, CardMarketPriceGuide } from './cardmarket/client.ts';
import { db } from './firebase.js';
import { doc, getDoc } from 'firebase/firestore';

export interface PriceData {
  market: 'cardmarket' | 'ebay';
  price: number;
  currency: string;
  condition?: string;
  lastUpdated: Date;
  url?: string;
}

export interface MarketListing {
  id: string;
  title: string;
  price: number;
  currency: string;
  condition?: string;
  seller?: string;
  url: string;
  imageUrl?: string;
  endTime?: Date;
}

export interface CardMarketData {
  prices: {
    averageSellPrice?: number;
    lowPrice?: number;
    trendPrice?: number;
    germanProLow?: number;
    suggestedPrice?: number;
    reverseHoloSell?: number;
    reverseHoloLow?: number;
    reverseHoloTrend?: number;
    lowPriceExPlus?: number;
    avg1?: number;
    avg7?: number;
    avg30?: number;
  };
  url?: string;
  updatedAt: Date;
}

export interface EBayData {
  recentSales: MarketListing[];
  activeListings: MarketListing[];
  averagePrice?: number;
  updatedAt: Date;
}

/**
 * Check if we should use real API or fallback to mock
 */
function shouldUseMockData(): boolean {
  // Use mock data if proxy URL is not configured
  return !process.env.REACT_APP_CARDMARKET_PROXY_URL;
}

/**
 * Generate mock price data for development/fallback
 */
function generateMockPriceData(cardId: string): CardMarketData {
  // Use card ID hash for consistent mock prices
  let hash = 0;
  for (let i = 0; i < cardId.length; i++) {
    const char = cardId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  const seed = Math.abs(hash) / 2147483647;

  const basePrice = seed * 50 + 5;
  return {
    prices: {
      averageSellPrice: basePrice,
      lowPrice: basePrice * 0.7,
      trendPrice: basePrice * 1.1,
      lowPriceExPlus: basePrice * 0.85,
      reverseHoloTrend: basePrice * 1.3,
    },
    url: `https://www.cardmarket.com/en/Pokemon/Products/Singles?searchString=${encodeURIComponent(cardId)}`,
    updatedAt: new Date(),
  };
}

// CardMarket API connector
export class CardMarketConnector {
  private cache = new Map<string, { data: CardMarketData; expires: Date }>();
  private cacheDuration = 60 * 60 * 1000; // 1 hour

  /**
   * Convert CardMarket API response to our format
   */
  private convertPriceGuide(priceGuide: CardMarketPriceGuide, url: string): CardMarketData {
    return {
      prices: {
        averageSellPrice: priceGuide.SELL,
        lowPrice: priceGuide.LOW,
        trendPrice: priceGuide.TREND,
        lowPriceExPlus: priceGuide.LOWEX,
        reverseHoloLow: priceGuide.LOWFOIL,
        reverseHoloTrend: priceGuide.TRENDFOIL,
      },
      url,
      updatedAt: new Date(),
    };
  }

  /**
   * Get cached price from Firestore if available
   */
  private async getCachedPrice(cardId: string): Promise<CardMarketData | null> {
    if (!db) return null;

    try {
      const priceRef = doc(db, 'prices', cardId);
      const priceSnap = await getDoc(priceRef);

      if (priceSnap.exists()) {
        const data = priceSnap.data();
        const latestPrice = data.latestPrice;
        const updatedAt = data.updatedAt?.toDate();

        // Check if cache is still valid (less than 24 hours old)
        if (updatedAt && Date.now() - updatedAt.getTime() < 24 * 60 * 60 * 1000) {
          return {
            prices: {
              averageSellPrice: latestPrice.averageSellPrice,
              lowPrice: latestPrice.lowPrice,
              trendPrice: latestPrice.trendPrice,
            },
            updatedAt,
          };
        }
      }
    } catch (error) {
      console.warn('Failed to get cached price:', error);
    }

    return null;
  }

  async getPriceData(
    cardId: string,
    cardName?: string,
    setName?: string,
    options?: { useCache?: boolean }
  ): Promise<CardMarketData | null> {
    const cacheKey = `cm_${cardId}`;

    // Check in-memory cache first
    if (options?.useCache !== false) {
      const cached = this.cache.get(cacheKey);
      if (cached && cached.expires > new Date()) {
        return cached.data;
      }
    }

    // Use mock data if API not configured
    if (shouldUseMockData()) {
      const mockData = generateMockPriceData(cardId);
      this.cache.set(cacheKey, {
        data: mockData,
        expires: new Date(Date.now() + this.cacheDuration),
      });
      return mockData;
    }

    try {
      // Check Firestore cache (populated by scheduled function)
      const cachedPrice = await this.getCachedPrice(cardId);
      if (cachedPrice) {
        this.cache.set(cacheKey, {
          data: cachedPrice,
          expires: new Date(Date.now() + this.cacheDuration),
        });
        return cachedPrice;
      }

      // Fetch from API if we have name and set
      if (cardName && setName) {
        const result = await cardMarketClient.getCardPrices(cardName, setName);
        if (result) {
          const data = this.convertPriceGuide(result.priceGuide, result.url);
          this.cache.set(cacheKey, {
            data,
            expires: new Date(Date.now() + this.cacheDuration),
          });
          return data;
        }
      }

      // Fallback to mock data
      const mockData = generateMockPriceData(cardId);
      this.cache.set(cacheKey, {
        data: mockData,
        expires: new Date(Date.now() + this.cacheDuration),
      });
      return mockData;
    } catch (error) {
      console.error('Failed to fetch CardMarket data:', error);
      // Return mock data on error
      return generateMockPriceData(cardId);
    }
  }

  async searchCard(query: string): Promise<MarketListing[]> {
    if (shouldUseMockData()) {
      return [
        {
          id: 'cm_mock_' + Date.now(),
          title: `${query} - Near Mint`,
          price: Math.random() * 50 + 5,
          currency: 'EUR',
          condition: 'Near Mint',
          seller: 'Mock Seller',
          url: `https://www.cardmarket.com/en/Pokemon/Products/Singles?searchString=${encodeURIComponent(query)}`,
        },
      ];
    }

    try {
      const results = await cardMarketClient.searchProducts(query, {
        game: 'pokemon',
        limit: 10,
      });

      return results.products.map((product) => ({
        id: `cm_${product.idProduct}`,
        title: product.enName,
        price: product.priceGuide?.TREND || 0,
        currency: 'EUR',
        condition: 'Various',
        url: product.website,
        imageUrl: product.image,
      }));
    } catch (error) {
      console.error('Failed to search CardMarket:', error);
      return [];
    }
  }
}

// eBay API connector
export class EBayConnector {
  private cache = new Map<string, { data: EBayData; expires: Date }>();

  async getMarketData(cardName: string, setName?: string, options?: { useCache?: boolean }): Promise<EBayData | null> {
    const searchQuery = setName ? `${cardName} ${setName}` : cardName;
    const cacheKey = `ebay_${searchQuery}`;
    
    // Check cache first
    if (options?.useCache !== false) {
      const cached = this.cache.get(cacheKey);
      if (cached && cached.expires > new Date()) {
        return cached.data;
      }
    }

    try {
      // Mock implementation - replace with actual eBay API call
      const mockListings: MarketListing[] = Array.from({ length: 5 }, (_, i) => ({
        id: `ebay_${i}_${Math.random()}`,
        title: `${searchQuery} - Pokemon Card`,
        price: Math.random() * 100 + 10,
        currency: 'USD',
        condition: ['Near Mint', 'Lightly Played', 'Moderately Played'][Math.floor(Math.random() * 3)],
        seller: `seller_${i + 1}`,
        url: `https://www.ebay.com/sch/i.html?_nkw=${encodeURIComponent(searchQuery)}`,
        endTime: new Date(Date.now() + Math.random() * 7 * 24 * 60 * 60 * 1000),
      }));

      const mockData: EBayData = {
        recentSales: mockListings.slice(0, 3),
        activeListings: mockListings,
        averagePrice: mockListings.reduce((sum, l) => sum + l.price, 0) / mockListings.length,
        updatedAt: new Date(),
      };

      // Cache for 30 minutes
      this.cache.set(cacheKey, {
        data: mockData,
        expires: new Date(Date.now() + 30 * 60 * 1000)
      });

      return mockData;
    } catch (error) {
      console.error('Failed to fetch eBay data:', error);
      return null;
    }
  }
}

// Singleton instances
export const cardMarketConnector = new CardMarketConnector();
export const eBayConnector = new EBayConnector();

// Unified price fetcher
export async function getCardPriceData(cardId: string, cardName: string, setName?: string): Promise<{
  cardmarket?: CardMarketData;
  ebay?: EBayData;
}> {
  const [cardMarketData, eBayData] = await Promise.allSettled([
    cardMarketConnector.getPriceData(cardId),
    eBayConnector.getMarketData(cardName, setName),
  ]);

  return {
    cardmarket: cardMarketData.status === 'fulfilled' ? cardMarketData.value || undefined : undefined,
    ebay: eBayData.status === 'fulfilled' ? eBayData.value || undefined : undefined,
  };
}
